import { Tween, Easing } from '@tweenjs/tween.js';
import AnimationClip from '../AnimationClip';
import TransformComponent from '../../../Component/Transform';
import Point from '../../../Utility/Point';
import SpriteComponent from '../../../Component/Render/Sprite';
import { delay } from '../../../Utility/Task/TaskQueue';
import Crop from '../../../Prototype/World/Crop';
import GameObject from '../../../GameObject/GameObject';
import Sprite from '../../../Prototype/Sprite';
import PIXIRenderer from '../../Render/PIXIRenderer';
import { MathHelper } from '../../../Utility/MathHelper';
import AnimationComponent from '../../../Component/Animation';
import { Random } from '../../../Utility/Random';
import { IHarvestSettings } from '../../../State/World/IFieldData';
import Button from '../../../Prototype/UI/BaseElements/Button';
import { DEG_TO_RAD } from 'pixi.js';

export interface ICropHarvestParams {
    texture: string;
    amplitude?: number;
    duration?: number;
    rewardTextures?: Random.IWeightedItem<string>[];
    transitionTexture?: string;
    iconSpawnData?: IHarvestSettings;
    destinationPoint?: Point;
}

// TODO: Break into 2 animations, 1 crop phase change and 1 crop reward flow
export class CropHarvest extends AnimationClip {
    public *execute(
        crop: Crop,
        meta?: ICropHarvestParams
    ): IterableIterator<number> {
        const harvestButton: Button = GameObject.findByName(
            'harvest_button_container'
        )[0] as Button;
        harvestButton.enabled = false;
        const amplitude: number = meta.amplitude ? meta.amplitude : 0.25;
        const duration: number = meta.duration ? meta.duration : 275;
        const mainSprite: GameObject = crop.cropSprite;
        const spriteTransform: TransformComponent = mainSprite.getComponent(
            TransformComponent
        );
        const sprite: SpriteComponent = mainSprite.getComponent(
            SpriteComponent
        );
        const baseScale: Point = new Point(1, 1); //spriteTransform.scale.clone();
        const maxScale: Point = new Point(
            baseScale.x * (1 + amplitude),
            baseScale.y * (1 + amplitude)
        );
        const cropScaleTween: Tween = this.getTween(
            spriteTransform.scale
        ).easing(Easing.Quadratic.InOut);

        // Set transitioning texture if available
        if (meta.transitionTexture) {
            sprite.texture = meta.transitionTexture;
        }

        // Create random delay to vary animation
        yield* delay(Math.random() * 500);

        if (meta.rewardTextures) {
            if (meta.iconSpawnData) {
                this.handleCropRewards(
                    crop,
                    meta.iconSpawnData,
                    meta.rewardTextures,
                    meta.destinationPoint
                );
            }
        }

        // Create random delay to vary animation
        yield* delay(Math.random() * 250);

        yield* delay(meta.transitionTexture ? 5000 : 0);

        // Bounce and disappear
        yield* this.scaleTweenTo(cropScaleTween, maxScale, duration);
        yield* this.scaleTweenTo(cropScaleTween, new Point(0, 0), duration);

        yield* delay(meta.rewardTextures && !meta.transitionTexture ? 5000 : 0);

        // Set new texture
        sprite.texture = meta.texture;

        // Bounce crop back
        yield* this.scaleTweenTo(cropScaleTween, maxScale, duration);
        yield* this.scaleTweenTo(cropScaleTween, baseScale, duration);

        // Delay until the reward tween is done
        if (meta.rewardTextures) {
            yield* delay(Math.max(10000 - duration * 4, 0));
        } else {
            yield* delay(750);
            harvestButton.enabled = true;
        }
    }
    private *scaleTweenTo(
        tween: Tween,
        scale: Point,
        delay: number
    ): IterableIterator<number> {
        tween.to({ x: scale.x, y: scale.y }, delay);
        yield* this.startTween(tween);
    }
    private createReward(
        gameObject: GameObject,
        texture: string,
        offset: Point = new Point(0, 0)
    ) {
        const position: Point = Point.add(
            gameObject
                .getComponent(TransformComponent)
                .position.clone() as Point,
            offset
        );
        const reward: GameObject = GameObject.Instantiate(Sprite, {
            position,
            texture,
            cullOffScreen: true,
            cullPadding: [15, 15, 15, 15],
            anchor: new Point(0.5, 0.5),
            scale: new Point(0, 0),
        });
        reward.addComponent(AnimationComponent);

        gameObject.parent.parent.addChild(reward);
        reward.getComponent(TransformComponent).position.copy(position);

        return reward;
    }
    private createRewards(
        gameObject: GameObject,
        cropSpawnData: IHarvestSettings,
        textures: Random.IWeightedItem<string>[]
    ): ICropRewardData[] {
        delay(Math.random() * 250);
        const increment: number = (Math.PI * 2) / cropSpawnData.count;
        const rewards: GameObject[] = [];
        for (let i = 0; i < cropSpawnData.count; i++) {
            const radius: number = cropSpawnData.radius * Random.value(0.5, 1);
            const position: Point = new Point(
                cropSpawnData.originPoint.x + radius * Math.cos(increment * i),
                cropSpawnData.originPoint.y + radius * Math.sin(increment * i)
            );

            const newReward: GameObject = this.createReward(
                gameObject,
                Random.getRandomWeighted(textures),
                position
            );
            rewards.push(newReward);
        }
        return rewards.map(gameObject => {
            const transform: TransformComponent = gameObject.getComponent(
                TransformComponent
            );
            const positionTween: Tween = null;
            const wiggleTween: Tween = this.getTween(transform)
                .repeat(Infinity)
                .yoyo(true)
                .easing(Easing.Sinusoidal.Out);
            const scaleThree: Tween = this.getTween(transform.scale).easing(
                Easing.Sinusoidal.Out
            );
            const scaleTwo: Tween = this.getTween(transform.scale)
                .easing(Easing.Sinusoidal.Out)
                .chain(scaleThree);
            const scaleOne: Tween = this.getTween(transform.scale)
                .easing(Easing.Sinusoidal.Out)
                .chain(scaleTwo);

            return {
                gameObject,
                scaleOne,
                scaleTwo,
                scaleThree,
                wiggleTween,
                positionTween,
            };
        });
    }
    private handleCropRewards(
        gameObject: GameObject,
        cropSpawnData: IHarvestSettings,
        textures: Random.IWeightedItem<string>[],
        destinationPoint: Point
    ) {
        const rewardData: ICropRewardData[] = this.createRewards(
            gameObject,
            cropSpawnData,
            textures
        );
        rewardData.forEach((reward: ICropRewardData) => {
            reward.positionTween = this.createBezierTween(
                reward.gameObject.getComponent(TransformComponent),
                destinationPoint
            );

            reward.positionTween.onComplete(() => reward.gameObject.destroy());

            // Choose a random size for the reward item
            const baseScale: number = Random.value(0.75, 1);
            // Scale to 130%
            reward.scaleOne.to({ x: 1.3 * baseScale, y: 1.5 * baseScale }, 333);
            // Scale back to 100%
            reward.scaleTwo.to({ x: 0.7 * baseScale, y: 0.7 * baseScale }, 250);
            reward.scaleThree
                .to({ x: 1 * baseScale, y: 1 * baseScale }, 333)
                .onComplete(() => {
                    reward.wiggleTween.stop();
                    reward.positionTween.start(this.elapsedTime);
                });

            reward.wiggleTween.to({ rotation: DEG_TO_RAD * 15 }, 100);
            reward.wiggleTween.start(this.elapsedTime);
            reward.scaleOne.start(this.elapsedTime);
        });
    }
    protected createBezierTween(
        transform: TransformComponent,
        destination: Point,
        duration: number = 2500
    ): Tween {
        const bezierTween = this.getTween({ progress: 0 });
        const center = PIXIRenderer.mapViewport.center.clone();
        const p1 = transform.position.clone() as Point;
        const p4 = destination;
        const p3 = new Point(
            center.x +
                PIXIRenderer.mapViewport.screenWidth / 2 +
                Random.value(-1, 1) *
                    (PIXIRenderer.mapViewport.screenWidth / 4),
            center.y +
                Random.value(-1, 1) *
                    (PIXIRenderer.mapViewport.screenHeight / 4)
        );
        const p2 = new Point(
            Random.sign() > 0
                ? center.x -
                  PIXIRenderer.mapViewport.screenWidth / 2 +
                  Random.value(-1, 1) *
                      (PIXIRenderer.mapViewport.screenWidth / 2)
                : center.x +
                  PIXIRenderer.mapViewport.screenWidth / 2 +
                  Random.value(-1, 1) *
                      (PIXIRenderer.mapViewport.screenWidth / 2),
            center.y +
                PIXIRenderer.mapViewport.screenHeight / 1.5 +
                Random.value(-1, 1) *
                    (PIXIRenderer.mapViewport.screenHeight / 6)
        );

        const rotationDirection: number = Random.sign();
        const distance: number = Math.abs(Point.distance(p1, p4));
        const delay: number =
            Math.max(distance - PIXIRenderer.mapViewport.screenWidth, 1000) / 2;

        duration = Random.value(0.5, 1) * 3000 + delay;

        bezierTween
            .to({ progress: 1 }, duration)
            .onUpdate(t => {
                const xy = MathHelper.cubicBezier(t.progress, p1, p2, p3, p4);
                transform.position.set(xy.x, xy.y);
                transform.rotation += rotationDirection * (Math.PI / 180) * 7;
            })
            .easing(Easing.Sinusoidal.In);

        return bezierTween;
    }
}

interface ICropRewardData {
    gameObject: GameObject;
    scaleOne: Tween;
    scaleTwo: Tween;
    scaleThree: Tween;
    wiggleTween: Tween;
    positionTween: Tween;
}
